<template>
  <h3>Mis gastos de saldo</h3>

  <div class="controlls">
    <a-form class="form" layout="vertical" :model="formState">
      <a-form-item>
        <!-- TODO: (observaciones beex) Cambiar linea :disabled-date="disabledDateRage && disabledDateRule" por :disabled-date="disabledDateRage" -->
        <a-range-picker
          v-model:value="formState.dateRange"
          format="DD/MM/YYYY"
          valueFormat="YYYY-MM-DD"
          :disabled-date="disabledDateRage && disabledDateRule"
          @calendarChange="handleCalendarChange"
        />
      </a-form-item>
    </a-form>

    <a-button :loading="downloading" @click="handleDownload">
      Descargar gastos <DownloadOutlined />
    </a-button>
  </div>

  <a-table
    size="middle"
    id="expenses-table"
    :scroll="scrollStyle"
    :loading="loading"
    :columns="smsExpensesColumns"
    :dataSource="expensesCurrentPage"
    :pagination="false"
    @change="handleTableChange"
  >
    <template #headerCell="{ title, column }">
      <template v-if="column.type === 'info'">
        {{ title }}

        <a-tooltip :title="column.info">
          <InfoCircleOutlined />
        </a-tooltip>
      </template>
    </template>

    <template #bodyCell="{ column, record, text, index }">
      <template v-if="column.dataIndex === 'date'">
        {{ dateTimeToSlashFormat(text, false) }}
      </template>

      <template v-if="column.dataIndex === 'identifier'">
        {{ record.channel_customer?.alias || record.channel_customer?.associate }} ({{
          record.channel_customer?.associate
        }})
      </template>

      <template v-if="index === tableSource.length - 1 && column.dataIndex === 'date'">
        <div ref="observerFlagRef" />
      </template>
    </template>

    <template #summary>
      <a-table-summary fixed>
        <a-table-summary-row>
          <a-table-summary-cell><strong>Total</strong></a-table-summary-cell>

          <a-table-summary-cell>
            {{ getTotalByCategory("total_quantity", 0) }}
          </a-table-summary-cell>

          <a-table-summary-cell> ${{ getTotalByCategory("total_price") }} </a-table-summary-cell>
        </a-table-summary-row>
      </a-table-summary>
    </template>
  </a-table>
</template>

<script setup>
import { ref, reactive, watch } from "vue";
import { InfoCircleOutlined, DownloadOutlined } from "@ant-design/icons-vue";
import _ from "lodash";
import dayjs from "dayjs";

import { smsExpensesColumns } from "@/app/settings/utils/columns";
import { fetchSmsExpenses, downloadSmsExpenses } from "@/app/settings/services";
import { useScrollResize } from "@/app/shared/composables/useScrollResize";
import { dateTimeToSlashFormat } from "@/app/shared/utils/dates";
import { downloadFile } from "@/app/shared/utils/file";
import { useDateRangeLimit } from "@/app/shared/composables/useDateRangeLimit";

const { scrollStyle } = useScrollResize({ headerMargin: 525, width: 1000 });

const emit = defineEmits(["loading"]);

const observerFlagRef = ref(null);

const todayDate = dayjs().format("YYYY-MM-DD");

const formState = reactive({
  billing_category: "",
  dateRange: [todayDate, todayDate],
});
const expensesCurrentPage = ref([]);

const loading = ref(false);
const currentTablePage = ref(1);

const tableSource = ref([]);
// const totalExpenses = ref(null);

const { handleCalendarChange, disabledDateRage } = useDateRangeLimit(31);

// TODO: (observaciones beex) Eliminar
const disabledDateRule = (current) => {
  const workspace = localStorage.getItem("workspace");
  if (workspace != "culqi") return current && current < new Date(2024, 7, 1);
  return false;
};

const getTotalByCategory = (key, fixedTo = 4) => {
  const categoryData = expensesCurrentPage.value.map((record) => record[key]);

  const totalSum = categoryData.reduce((prev, curr) => prev + curr, 0);

  return Number(totalSum.toFixed(fixedTo));
};

const fetchExpensesTableData = async ({ resetFilters = true } = {}) => {
  if (resetFilters) loading.value = true;

  if (!resetFilters) pause();

  const filters = _.cloneDeep(formState);

  if (!filters.dateRange || filters.dateRange.length < 2) {
    expensesCurrentPage.value = [];
    loading.value = false;
    return;
  }
  
  filters.dateRange[1] = `${filters.dateRange[1]} 23:59:59`;

  emit('loading', true);
  try {
    expensesCurrentPage.value = await fetchSmsExpenses({
      filters: filters,
      page: currentTablePage.value,
    });
  }
  finally {
    emit('loading', false);
  }

  loading.value = false;
};

const downloading = ref(false);

const handleDownload = async () => {
  downloading.value = true;

  try {
    const filters = _.cloneDeep(formState);
    filters.dateRange[1] = `${filters.dateRange[1]} 23:59:59`;

    const response = await downloadSmsExpenses({ filters });

    downloadFile(response, "expenses.xlsx", { useResponseFileName: true });
  } catch (error) {
    console.error(error);
  }

  downloading.value = false;
};

watch(
  formState,
  _.debounce(() => {
    currentTablePage.value = 1;
    fetchExpensesTableData();
  }, 500),
  { immediate: true }
);

defineExpose({ fetchExpensesTableData });
</script>

<style lang="sass" scoped>
.controlls
  margin-bottom: 12px
  display: flex
  align-items: center
  justify-content: space-between

.form
  display: flex
  gap: 12px

  .input
    min-width: 160px

:deep(.ant-form-item)
  margin: 0

#expenses-table tfoot th,
#expenses-table tfoot td
  background-color: #fafafa !important
</style>
